import { useContext, useEffect } from 'react';
import { Navigate, Outlet, useMatches, useLocation, useParams, useNavigate } from 'react-router';
import { useIntercom } from 'react-use-intercom';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { useLDClient, withLDConsumer } from 'launchdarkly-react-client-sdk';
import cube from '@cubejs-client/core';
import { CubeProvider } from '@cubejs-client/react';

import { HeadquarterContext } from 'context/HeadquarterContext';

import { useAuth } from 'hooks/useAuth';
import { useHasLocationScope } from 'hooks/useHasLocationScope';
import { STATUSES } from 'hooks/useLocation';
import useDataDogContextAttributes from 'hooks/useDataDogContextAttributes';

import { RESTAURANT_ONBOARDING_WIZARD } from 'scopes/scopes';
import { getUpdatedLocationPath } from 'scopes/nav';

import { initLaunchDarklyUser, initLaunchDarklyCubohUser } from 'helpers/featureFlags';
import { isInternalAdminPage } from 'helpers/routeMatches';
import { isEmpty } from 'helpers/utils';
import { getLatestTokens } from 'helpers/auth.cuboh';

import { LEGACY_DASHBOARD_URL } from 'constants/chownow';
import {
  ACCOUNT_SETTINGS_PATH,
  CUBOH_COMPANY_PREFIX,
  CUSTOMIZATIONS_PATH,
  EMAIL_NOTIFICATIONS_PATH,
  HQ_DETAILS_PATH,
  LOCATION_PATH,
  USERS_PATH,
  WEBSITE_ACCESS_PATH,
} from 'constants/path';
import { ERROR_PAGE_BUTTONS, ERROR_PAGE_SUBTITLES, ERROR_PAGE_TITLES } from 'constants/error';

import { Anchor, AppShell, Container, Divider, Flex, LoadingOverlay, Text } from '@mantine/core';
import ErrorPage from 'components/Errors/ErrorPage/ErrorPage';
import { Footer } from 'components/Footer';
import NavGlobal from 'components/NavGlobal/NavGlobal';
import NavImpersonating from 'components/NavImpersonating/NavImpersonating';
import { NavInternal } from 'components/NavInternal';
import { NavLeft } from 'components/NavLeft';
import ProtectedNotificationBanners from 'components/Notifications/ProtectedNotificationBanners';
import OnboardingBanner from 'components/OnboardingBanner/OnboardingBanner';
import { ReturnToLinkHandler } from 'components/ReturnToLink/ReturnToLinkHandler';

import ErrorBoundary from 'errorBoundary/ErrorBoundary';
import { ErrorBoundaryError, ErrorBoundaryUnauthorizedError } from 'errorBoundary/errors';

import { ChowNowHorizontalLogoWhite, ChowNowMarkLogoWhite } from '@chownow/pantry/logos';

import { breakpoints } from 'styles/theme.variables';

// Created once, outside the component. The token is fetched per request.
const cubeApi = cube(async () => (await getLatestTokens()).reportingToken, {
  apiUrl: import.meta.env.VITE_CUBE_URL,
});

const SHOW_FOOTER_PATHS = [
  ACCOUNT_SETTINGS_PATH,
  CUSTOMIZATIONS_PATH,
  EMAIL_NOTIFICATIONS_PATH,
  USERS_PATH,
  WEBSITE_ACCESS_PATH,
];

/** handles statuses from useLocation to show error pages  */
function LocationStatusProtector({ children }) {
  const { location, locationStatus, isCompanyUnauthorized } = useContext(HeadquarterContext);
  const { restaurantId } = useParams();

  // Only check locationStatus for restaurant routes — on non-restaurant routes (e.g. /),
  // the stale UNAUTHORIZED status from a previously-selected restaurant should not block rendering
  if (isCompanyUnauthorized || (restaurantId && locationStatus === STATUSES.UNAUTHORIZED)) {
    throw new ErrorBoundaryUnauthorizedError({});
  } else if (!location && locationStatus === STATUSES.ERRORED) {
    throw new ErrorBoundaryError({ hasError: true });
  }

  return children;
}

function ProtectedLayout() {
  useDataDogContextAttributes();

  const ldClient = useLDClient();
  const shouldShowOnboardingBanner = useHasLocationScope(RESTAURANT_ONBOARDING_WIZARD);
  const { companyId, restaurantId } = useParams();
  const navigate = useNavigate();
  const [sideMenuOpened, sideMenuHandlers] = useDisclosure();
  const matches = useMatches();

  const {
    cubohUser,
    isUserCubohOnly,
    user,
    isInitialLoad,
    isLoading: isUserLoading,
    isInternalUser,
    isImpersonating,
    isCorpaImpersonation,
  } = useAuth();
  const { isHqLoading, headquarter, locationStatus, isCompanyUnauthorized } =
    useContext(HeadquarterContext);
  const hasCubeClient = Boolean(user) && !user.is_cuboh_user;
  const { boot } = useIntercom();
  const { state: locationState, pathname } = useLocation();

  const showImpersonatingBanner = isImpersonating && isCorpaImpersonation;
  const isAdminPage = isInternalAdminPage(matches);
  const currentRoute = matches[matches.length - 1];
  const shouldHideAllNav = Boolean(currentRoute.handle?.isLinkOut);
  const shouldShowFooter =
    SHOW_FOOTER_PATHS.some(path => currentRoute.pathname.includes(path)) ||
    // Match specifically the location details page and HQ details only, not any nested paths
    currentRoute.pathname === `${LOCATION_PATH}/${restaurantId}/` ||
    currentRoute.pathname === `${HQ_DETAILS_PATH}/${companyId}/`;
  const showOnBoardingBanner = !isAdminPage && shouldShowOnboardingBanner;

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.maxMobile})`);

  const fallbackErrorPage = (
    <ErrorPage
      title={ERROR_PAGE_TITLES.general}
      subtitle={ERROR_PAGE_SUBTITLES.general}
      buttonCta={ERROR_PAGE_BUTTONS.startExploring}
    />
  );

  useEffect(() => {
    sideMenuHandlers.close();
  }, [pathname]);

  useEffect(() => {
    if (isUserCubohOnly && restaurantId && !restaurantId?.includes(CUBOH_COMPANY_PREFIX)) {
      navigate(
        getUpdatedLocationPath({
          pathname: currentRoute.pathname,
          id: restaurantId,
          prevId: restaurantId,
          isUserCubohOnly,
        }),
      );
    }
  }, [isUserCubohOnly, restaurantId, currentRoute.pathname]);

  // shows intercom in the case of a re-login
  useEffect(() => {
    if (isUserCubohOnly && cubohUser) {
      boot({
        name: cubohUser?.username,
        userId: cubohUser?.intercomID,
        hideDefaultLauncher: false,
        alignment: 'right',
        horizontalPadding: 20,
        verticalPadding: 20,
        createdAt: new Date(),
        userHash: cubohUser?.intercomHmac,
      });
    }
  }, [isUserCubohOnly, cubohUser]);

  useEffect(() => {
    if (user && !user.is_cuboh_user) {
      initLaunchDarklyUser(
        { ...user, currentRestaurant: restaurantId, currentCompany: headquarter?.id },
        ldClient,
      );
    } else if (user?.is_cuboh_user && cubohUser) {
      initLaunchDarklyCubohUser(
        {
          cubohUser,
          currentRestaurant: restaurantId,
          currentCompany: headquarter?.id,
          isImpersonating,
        },
        ldClient,
      );
    }
  }, [user, cubohUser, restaurantId, headquarter, isImpersonating]);

  if (isEmpty(user) && !isInitialLoad) {
    const currentPath = window.location.pathname;
    const queryParams = window.location.search;
    const fullPath = currentPath + queryParams;

    // If user logs out intentionally, take them to login without next param
    if (currentPath === '/logout') {
      return <Navigate to="/login" replace state={{ isCleanLogout: true }} />;
    }

    const isCleanLogout = locationState?.isCleanLogout;
    if (isCleanLogout) {
      return <Navigate to="/login" replace />;
    }

    if (currentPath === '/login') {
      return null;
    }

    // Take user to login with next param if attempting to access page while unauthed
    return <Navigate to={`/login?next=${encodeURIComponent(fullPath)}`} replace />;
  }

  if ((isHqLoading || isInitialLoad || isUserLoading) && !user?.is_cuboh_user) {
    return <LoadingOverlay visible />;
  }

  if (shouldHideAllNav) {
    return (
      <AppShell header={{ height: 'var(--dimension-xl4)' }}>
        <AppShell.Header
          style={{
            backgroundColor: 'var(--surface-accent-primary)',
            color: 'var(--content-accent-secondary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 var(--mantine-spacing-xl)',
          }}
        >
          {isMobile ? (
            <ChowNowMarkLogoWhite height="32" width="29" />
          ) : (
            <Flex align="center">
              <ChowNowHorizontalLogoWhite height="32" width="126" />
              <Divider orientation="vertical" h={34} mx="sm" />
              <Text variant="caption-md">{headquarter?.name}</Text>
            </Flex>
          )}
          <Anchor
            size="md"
            underline="always"
            variant="sublabel"
            c="var(--content-accent-secondary)"
            href={LEGACY_DASHBOARD_URL}
          >
            Back to Dashboard
          </Anchor>
        </AppShell.Header>
        <AppShell.Main
          pl={{ sm: 'sm', md: 'md', lg: 'xl' }}
          pr={{ sm: 'sm', md: 'md', lg: 'xl' }}
          w="100%"
        >
          <CubeProvider cubeApi={hasCubeClient ? cubeApi : null}>
            <ErrorBoundary fallback={fallbackErrorPage}>
              <LocationStatusProtector>
                <Outlet />
              </LocationStatusProtector>
            </ErrorBoundary>
          </CubeProvider>
        </AppShell.Main>
      </AppShell>
    );
  }

  return (
    <AppShell
      header={{
        height:
          isInternalUser || showImpersonatingBanner
            ? { sm: 'var(--dimension-xl2)', md: 'var(--dimension-xl3)' }
            : 0,
      }}
      navbar={{
        width: isAdminPage ? 0 : 200,
        breakpoint: breakpoints.maxMobile,
        visibleFrom: breakpoints.maxMobile,
      }}
      aside={{
        breakpoint: 'md',
        collapsed: { desktop: true, mobile: !sideMenuOpened },
      }}
    >
      {isInternalUser && (
        <AppShell.Header zIndex="var(--mantine-z-index-modal)">
          <NavInternal />
        </AppShell.Header>
      )}
      {showImpersonatingBanner && (
        <AppShell.Header zIndex="var(--mantine-z-index-modal)">
          <NavImpersonating />
        </AppShell.Header>
      )}
      {!isAdminPage && (
        <NavLeft sideMenuOpened={sideMenuOpened} toggleSideMenu={sideMenuHandlers.toggle} />
      )}
      <AppShell.Main style={{ display: 'flex', flexDirection: 'column' }}>
        {(headquarter || user?.is_cuboh_user) && (
          <NavGlobal toggleSideMenu={sideMenuHandlers.toggle} />
        )}
        <Container
          fluid
          px={{ sm: 'sm', md: 'md', lg: 'xl' }}
          pb="xl"
          pt={
            (restaurantId && locationStatus === STATUSES.UNAUTHORIZED) || isCompanyUnauthorized
              ? { sm: 'sm', md: 'md', lg: 'xl' }
              : undefined
          }
          w="100%"
          style={{ display: 'flex', flexDirection: 'column', flexGrow: '1' }}
        >
          <ProtectedNotificationBanners />
          {showOnBoardingBanner && <OnboardingBanner />}
          <CubeProvider cubeApi={hasCubeClient ? cubeApi : null}>
            <ErrorBoundary fallback={fallbackErrorPage}>
              <LocationStatusProtector>
                <ReturnToLinkHandler>
                  <Outlet />
                </ReturnToLinkHandler>
              </LocationStatusProtector>
            </ErrorBoundary>
          </CubeProvider>
        </Container>
        {shouldShowFooter && <Footer />}
      </AppShell.Main>
    </AppShell>
  );
}

export default withLDConsumer()(ProtectedLayout);
